import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import round from 'lodash/round';
import maxBy from 'lodash/maxBy';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { selectMedianData, selectChartData, selectMedianDifferenceData } from '../../../../utils';
import SplitContentContainer from '../SplitContentContainer/SplitContentContainer';
import SRLDifferenceIndicator from '../SRLDifferenceIndicator/SRLDifferenceIndicator';
import SRLKeyNumber from '../SRLKeyNumber/SRLKeyNumber';
import styles from './SRLHoursContent.module.css';

const HourTick = ({ ...props }) => {
  const { x, y, payload } = props;
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="middle"
        fill="var(--prs-c-gray-700)"
        fontSize="14px"
      >
        {payload.value}h
      </text>
    </g>
  );
};

const renderHoursChart = (chartData, height) => {
  // Filter out any student_count of 0
  // Pull out text after the interval start so we just get one number value
  const filteredChartData = chartData.reduce((acc, { answer, order_by, student_count }) => {
    if (student_count !== 0) {
      acc.push({
        answer: answer.split('-')[0],
        order_by,
        student_count
      });
    }
    return acc;
  }, []);

  // Only allow max of 11 columns
  // If more, aggregate student_count into the 11th column
  if (filteredChartData.length > 11) {
    const outlierIndex = 10;
    const outlierHour = filteredChartData[outlierIndex].answer;
    const sumStudentCount = filteredChartData.slice(outlierIndex).reduce((sum, { student_count }) => sum + student_count, 0);
    filteredChartData[outlierIndex] = { answer: `>${outlierHour}`, order_by: 100, student_count: sumStudentCount };
    filteredChartData.length = 11;
  }

  let highDomainLimit = 40;
  const maxData = maxBy(filteredChartData, 'student_count');
  if (maxData) {
    highDomainLimit = Number(maxData.student_count);
  }

  return (
    <ResponsiveContainer height={height}>
      <BarChart
        data={filteredChartData}
        margin={{ top: 20, right: 0, left: -20, bottom: 20 }}
        barCategoryGap={1}
      >
        <Bar
          dataKey="student_count"
          fill="var(--prs-c-info-800)"
          label={{
            value: 'student_count',
            position: 'top',
            fontFamily: 'Source Sans Pro',
            fontSize: 14,
            fill: '#242424' }}
        />
        <XAxis
          dataKey="answer"
          tickLine={false}
          label={{ value: 'hours per week',
            fontSize: 14,
            fontFamily: 'Source Sans Pro',
            fontStyle: 'italic',
            fill: '#242424',
            offset: 0,
            position: "bottom",
          }}
          tick={HourTick}
        />
        <YAxis
          domain={[0, highDomainLimit]}
          label={{ value: 'total students',
            angle: -90,
            position: 'center',
            fontSize: 14,
            fontFamily: 'Source Sans Pro',
            fontStyle: 'italic',
            fill: '#242424',
          }}
          tick={false}
          axisLine={false}
          tickLine={false}
        />
        <CartesianGrid vertical={false} horizontal={false} />
      </BarChart>
    </ResponsiveContainer>
  );
};

const renderMedian = (medianData, differenceData) => (
  <div className={styles.containerPadding}>
    <SRLKeyNumber
      keyNumber={round(medianData.median, 1)}
      postScript="h"
      label="class median"
    />
    {differenceData &&
      <SRLDifferenceIndicator
        lastAssignmentValue={differenceData.last_assignment}
        thisAssignmentValue={differenceData.this_assignment}
        unitOfMeasurement="h"
        numberOfDecimals={1}
      />
    }
  </div>
);

function SRLHoursContent({ data, isMobileXS }) {
  const { insightsData, isSRLIntroSurvey } = data;

  const medianData = selectMedianData(insightsData);
  const chartData = selectChartData(insightsData);

  let rightColumnContent = null;
  let leftColumnContent = null;
  let differenceData = null;
  if (!isSRLIntroSurvey) {
    differenceData = selectMedianDifferenceData(insightsData);
  }

  if (!isEmpty(medianData)) {
    leftColumnContent = renderMedian(medianData, differenceData);
  }

  if (chartData) {
    rightColumnContent = renderHoursChart(chartData, 240);
  }
  return (
    <SplitContentContainer
      leftColumnContent={leftColumnContent}
      rightColumnContent={rightColumnContent}
      borderPosition="left"
      stackedVersion={isMobileXS}
    />
  );
}

SRLHoursContent.propTypes = {
  data: PropTypes.shape({
    insightsData: PropTypes.array.isRequired,
    isSRLIntroSurvey: PropTypes.bool,
  }),
  isMobileXS: PropTypes.bool
};

export default SRLHoursContent;
